"use client"

import React from "react"
import { styled, alpha } from "@mui/material/styles"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useTheme } from "@mui/material/styles"
import Slide from "@mui/material/Slide"
import { Box, Typography } from "@mui/material"

// Enhanced slide transition with better timing
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 300 }} />
})

// Styled Dialog with enhanced aesthetics
const StylishDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.2), 0 8px 10px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "all 0.3s ease",
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.05),
    backdropFilter: "blur(8px)",
    background: alpha(theme.palette.background.paper, 0.95),
  },
  /*"& .MuiBackdrop-root": {
    backdropFilter: "blur(4px)",
    backgroundColor: alpha(theme.palette.background.default, 0.7),
  },*/
  "& .MuiDialogContent-root": {
    padding: theme.spacing(0),
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: alpha(theme.palette.divider, 0.05),
      borderRadius: "10px",
    },
  },
  "&.MuiDialog-paperFullScreen": {
    borderRadius: 0,
  },
}))

// Styled DialogTitle with better typography and spacing
const StylishDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: alpha(theme.palette.primary.main, 0.03),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}))

// Styled close button with hover effects
const CloseButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  transition: "all 0.2s ease",
  color: theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
    transform: "rotate(90deg)",
  },
}))

const DetailDialog = ({ open, onClose, title = null, children, fullScreen = false }) => {
  const theme = useTheme()
  const isFullScreen = useMediaQuery(theme.breakpoints.down("sm")) || fullScreen

  return (
    <StylishDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullScreen={isFullScreen}
      TransitionComponent={Transition}
      maxWidth="md"
    >
      {title && (
        <StylishDialogTitle disableTypography id="customized-dialog-title">
          <Typography variant="h6" component="span" fontWeight="600">
            {title}
          </Typography>
          {onClose && <CloseButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </CloseButton>}
        </StylishDialogTitle>
      )}

      {!title && (
        <Box sx={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}>
          <CloseButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </CloseButton>
        </Box>
      )}

      <DialogContent dividers={!!title}>{children}</DialogContent>
    </StylishDialog>
  )
}

export default DetailDialog

